import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import ChangePasswordModal from './ChangePasswordModal';

function Nav({ darkMode, onToggleTheme }) {
  const { user, role, loading, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard', roles: ['admin'] },
    { to: '/mentors', label: 'Mentors', roles: ['admin'] },
    { to: '/students', label: 'Students', roles: ['admin'] },
    { to: '/students/import', label: 'Import', roles: ['admin'] },
    { to: '/mentor', label: 'Dashboard', roles: ['mentor'] },
    { to: '/mentor/students', label: 'My Students', roles: ['mentor'] },
    { to: '/calls', label: 'Call Logs', roles: ['admin', 'mentor'] },
  ];

  const visibleLinks = navLinks.filter((link) => {
    return link.roles && role && link.roles.includes(role);
  });

  const isActive = (path) => {
    if (path === '/dashboard' && location.pathname === '/') return true;
    return location.pathname === path;
  };

  return (
    <nav className="sticky top-0 z-40 border-b" style={{
      background: darkMode ? 'rgba(11, 17, 32, 0.85)' : 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(16px) saturate(180%)',
      WebkitBackdropFilter: 'blur(16px) saturate(180%)',
      borderColor: darkMode ? 'var(--color-border)' : 'var(--color-border)',
    }}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Left: Logo + Nav */}
          <div className="flex items-center gap-1 md:gap-6">
            {/* Mobile menu */}
            <button
              className="md:hidden p-2.5 -ml-2 rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800 transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                )}
              </svg>
            </button>

            <Link to="/" className="flex items-center gap-2 mr-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-brand)' }}>
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342" />
                </svg>
              </div>
              <span className="hidden sm:inline text-sm font-semibold text-slate-800 dark:text-white" style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }}>
                Mentor Portal
              </span>
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-0.5">
              {visibleLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative text-xs font-medium px-3 py-1.5 rounded-md transition-all duration-150 ${
                    isActive(link.to)
                      ? 'text-brand bg-brand/8 dark:bg-brand/12'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onToggleTheme}
              className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:bg-white/5 transition-colors"
              title={darkMode ? 'Light mode' : 'Dark mode'}
            >
              {darkMode ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
                </svg>
              )}
            </button>

            {/* Desktop user area */}
            <div className="hidden md:flex items-center gap-2 ml-1">
              {user && !loading && (
                <div className="text-right mr-1">
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300 leading-tight truncate max-w-[160px]">
                    {user.email}
                  </p>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    {role || 'member'}
                  </p>
                </div>
              )}
              {user && (
                <>
                  <button
                    type="button"
                    onClick={() => setIsChangePasswordOpen(true)}
                    className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:bg-white/5 transition-colors"
                    title="Change Password"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => signOut().catch((err) => console.error('Failed to sign out', err))}
                    className="text-xs font-medium px-2.5 py-1 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 dark:text-slate-400 dark:hover:text-red-400 dark:hover:bg-red-500/10 transition-colors"
                  >
                    Log out
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden pb-3 pt-1 border-t border-slate-100 dark:border-slate-800 animate-fade-in">
            <div className="space-y-0.5 py-2">
              {visibleLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block text-sm font-medium px-3 py-2 rounded-md transition-colors ${
                    isActive(link.to)
                      ? 'text-brand bg-brand/8'
                      : 'text-slate-600 hover:text-brand hover:bg-brand/5 dark:text-slate-300 dark:hover:bg-brand/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {user && (
              <div className="pt-2 mt-2 border-t border-slate-100 dark:border-slate-800">
                {!loading && (
                  <div className="px-3 mb-2">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {user.email}
                    </p>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      {role || 'member'}
                    </p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => { setIsMenuOpen(false); setIsChangePasswordOpen(true); }}
                  className="w-full text-left px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800 rounded-md transition-colors"
                >
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={() => { setIsMenuOpen(false); signOut().catch(console.error); }}
                  className="w-full text-left px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 rounded-md transition-colors"
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
    </nav>
  );
}

export default Nav;
